/**
 * Purchase funnel shared helpers — step ordering, price formatting, and the
 * sessionStorage hand-offs between funnel steps (plan deep link + product
 * selection).
 *
 * Design: docs/technical-design-website-product.md §4.1
 */
import type { WhoFor } from '@/types';

export type FunnelStep =
  | 'account'
  | 'agreement'
  | 'products'
  | 'shipping'
  | 'payment'
  | 'confirmation';

export const FUNNEL_STEP_ORDER: Record<FunnelStep, number> = {
  account: 1,
  agreement: 2,
  products: 3,
  shipping: 4,
  payment: 5,
  confirmation: 6,
};

export const FUNNEL_STEP_PATHS: Record<FunnelStep, string> = {
  account: '/start/account',
  agreement: '/start/agreement',
  products: '/start/products',
  shipping: '/start/shipping',
  payment: '/start/payment',
  confirmation: '/start/confirmation',
};

/** The six progress-bar labels, in order. "Shipping" added 2026-06 (86baetyga);
 * step "App Access" renamed 2026-06-12: no manual account-approval process. */
export const FUNNEL_STEP_LABELS = [
  'Create Account',
  'Review Agreement',
  'Product Selection',
  'Shipping',
  'Payment',
  'App Access',
] as const;

/**
 * Marketing site origin — used to build cross-site links (support, download,
 * products, account deletion). Override per-environment via VITE_MARKETING_URL.
 */
// Trailing slashes are stripped so `${MARKETING_SITE_URL}/path` can never
// produce a `//path` URL (breaks the marketing CloudFront rewrite).
export const MARKETING_SITE_URL: string = (
  import.meta.env.VITE_MARKETING_URL || 'https://legacyorb.com'
).replace(/\/+$/, '');

/**
 * Where "contact support" links point (review F15) — the dedicated /support
 * page on the marketing site (design §3.5 / delivery plan item 7).
 */
export const SUPPORT_URL = `${MARKETING_SITE_URL}/support`;

export const formatPrice = (cents: number) =>
  (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

/** Human labels for users.accountStatus (design §2.5).
 * Manual approval reinstated (ClickUp 86baab6dk): payment moves the account
 * to 'pending' until the team prepares it and assigns the story persona. */
export const ACCOUNT_STATUS_LABELS: Record<
  'pending' | 'approved' | 'active' | 'completed',
  string
> = {
  pending: 'Pending approval',
  approved: 'Approved',
  active: 'Active',
  completed: 'Completed',
};

// ── sessionStorage hand-offs ─────────────────────────────────────────────────

const PLAN_KEY = 'legacyorb.funnel.plan';
const SELECTION_KEY = 'legacyorb.funnel.selection';
const SHIPPING_KEY = 'legacyorb.funnel.shipping';
const PENDING_INTENT_KEY = 'legacyorb.funnel.pendingIntent';

/** ?plan= slug captured on /start, used to preselect the package later. */
export function storePreferredPlanSlug(slug: string) {
  try {
    sessionStorage.setItem(PLAN_KEY, slug);
  } catch {
    // Storage unavailable (private mode etc.) — preselection is best-effort.
  }
}

export function readPreferredPlanSlug(): string | null {
  try {
    return sessionStorage.getItem(PLAN_KEY);
  } catch {
    return null;
  }
}

export interface FunnelSelection {
  offeringId: string;
  addonIds: string[];
}

/** Product selection handed from /start/products to /start/payment. */
export function storeFunnelSelection(selection: FunnelSelection) {
  try {
    sessionStorage.setItem(SELECTION_KEY, JSON.stringify(selection));
  } catch {
    // Best-effort — the payment page redirects back to products if missing.
  }
}

export function readFunnelSelection(): FunnelSelection | null {
  try {
    const raw = sessionStorage.getItem(SELECTION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<FunnelSelection>;
    if (typeof parsed.offeringId !== 'string' || !parsed.offeringId) return null;
    return {
      offeringId: parsed.offeringId,
      addonIds: Array.isArray(parsed.addonIds)
        ? parsed.addonIds.filter((id): id is string => typeof id === 'string')
        : [],
    };
  } catch {
    return null;
  }
}

export function clearFunnelSelection() {
  try {
    sessionStorage.removeItem(SELECTION_KEY);
  } catch {
    // Ignore.
  }
}

/**
 * US states + DC for the shipping address State/region dropdown. We ship to the
 * US only for now (Country is fixed to "United States"), so the region is a
 * closed list rather than free text — `region` is stored as the 2-letter USPS
 * code (e.g. "CT", "CA", "NY"), which is what mailing labels expect.
 */
export const US_STATES: ReadonlyArray<{ code: string; name: string }> = [
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'DC', name: 'District of Columbia' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
] as const;

/** Valid USPS state codes, for validating a stored/typed region. */
export const US_STATE_CODES: ReadonlySet<string> = new Set(US_STATES.map((s) => s.code));

/** The only country we ship to for now — Country field is fixed to this value. */
export const DEFAULT_COUNTRY = 'United States';

/**
 * Shipping address captured on /start/shipping and handed to /start/payment.
 * Mirrors shippingAddressSchema (shared-schemas) — kept as a local interface so
 * the web app doesn't depend on the shared-schemas package directly.
 */
export interface ShippingAddress {
  recipientName: string;
  line1: string;
  line2?: string;
  city: string;
  region: string;
  postalCode: string;
  country: string;
  phone?: string;
}

export function storeShippingAddress(address: ShippingAddress) {
  try {
    sessionStorage.setItem(SHIPPING_KEY, JSON.stringify(address));
  } catch {
    // Best-effort — payment re-derives whether shipping is required and bounces
    // back to /start/shipping if a physical order is missing one.
  }
}

export function readShippingAddress(): ShippingAddress | null {
  try {
    const raw = sessionStorage.getItem(SHIPPING_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ShippingAddress>;
    // The required fields must all be present to be a usable address.
    if (
      !parsed.recipientName ||
      !parsed.line1 ||
      !parsed.city ||
      !parsed.region ||
      !parsed.postalCode ||
      !parsed.country
    ) {
      return null;
    }
    return {
      recipientName: parsed.recipientName,
      line1: parsed.line1,
      line2: parsed.line2 || undefined,
      city: parsed.city,
      region: parsed.region,
      postalCode: parsed.postalCode,
      country: parsed.country,
      phone: parsed.phone || undefined,
    };
  } catch {
    return null;
  }
}

export function clearShippingAddress() {
  try {
    sessionStorage.removeItem(SHIPPING_KEY);
  } catch {
    // Ignore.
  }
}

/**
 * Intent selections captured on the combined signup card (step 1) before the
 * Clerk e-mail verification round-trip. Persisted to sessionStorage right
 * before signUp.create so that, once the user is signed in and lands back on
 * /start/account, the page can auto-POST /funnel/intent without showing a
 * second form.
 */
export interface PendingFunnelIntent {
  whoFor: WhoFor;
  /** null = "Choose later". */
  preferredOfferingSlug: string | null;
  /** Always true — the consent checkbox is required to submit. */
  consentAccepted: true;
}

export function storePendingFunnelIntent(intent: PendingFunnelIntent) {
  try {
    sessionStorage.setItem(PENDING_INTENT_KEY, JSON.stringify(intent));
  } catch {
    // Storage unavailable (private mode etc.) — the signed-in intent form
    // remains as a fallback, so account creation still proceeds.
  }
}

export function readPendingFunnelIntent(): PendingFunnelIntent | null {
  try {
    const raw = sessionStorage.getItem(PENDING_INTENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PendingFunnelIntent>;
    if (
      parsed.whoFor !== 'myself' &&
      parsed.whoFor !== 'parent_grandparent' &&
      parsed.whoFor !== 'gift'
    ) {
      return null;
    }
    if (parsed.consentAccepted !== true) return null;
    return {
      whoFor: parsed.whoFor,
      preferredOfferingSlug:
        typeof parsed.preferredOfferingSlug === 'string' ? parsed.preferredOfferingSlug : null,
      consentAccepted: true,
    };
  } catch {
    return null;
  }
}

export function clearPendingFunnelIntent() {
  try {
    sessionStorage.removeItem(PENDING_INTENT_KEY);
  } catch {
    // Ignore.
  }
}
